import { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button
} from '@material-ui/core';
import PageTitle from '../../components/PageTitle';
import Widget from '../../components/Widget/Widget';
import Notification from '../../utils/notification';
import api from '../../services/api';
import { getError } from '../../utils/getResponseError';
import { useLayoutDispatch, toggleLoading } from '../../context/LayoutContext';
import {
  parseCertificate,
  validatePrivateKey,
  readFileAsText,
} from '../../utils/certificateParser';
import ConfirmUpdateDialog from '../../components/ConfirmUpdateDialog/ConfirmUpdateDialog';
import useStyles from './configuracoesBancarias.styles';

// TODO: RBAC — redirect non-admin users
export default function ConfiguracoesBancarias() {
  const classes = useStyles();
  const layoutDispatch = useLayoutDispatch();

  const [configId, setConfigId] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [certFile, setCertFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [certExpiration, setCertExpiration] = useState(null);
  const [keyValid, setKeyValid] = useState(false);
  const [certError, setCertError] = useState(null);
  const [keyError, setKeyError] = useState(null);

  // Current status from server
  const [currentClientId, setCurrentClientId] = useState('');
  const [currentClientSecret, setCurrentClientSecret] = useState('');
  const [currentCertExpiration, setCurrentCertExpiration] = useState(null);
  const [currentKeyConfigured, setCurrentKeyConfigured] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    toggleLoading(layoutDispatch);
    try {
      const { data } = await api.get('/configuracoes-bancarias');
      if (data.length > 0) {
        const config = data[0];
        setConfigId(config.id_configuracao_bancaria);
        setClientId(config.client_id || '');
        setCurrentClientId(config.client_id || '');
        setCurrentClientSecret(config.client_secret || '');
        setCurrentCertExpiration(config.certificado_expiracao);
        setCurrentKeyConfigured(!!config.chave_privada_configurada);
      }
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
    }
    toggleLoading(layoutDispatch);
  }

  async function handleCertFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    setCertError(null);
    setCertExpiration(null);

    try {
      const text = await readFileAsText(file);
      const result = parseCertificate(text);
      setCertFile(file);
      setCertExpiration(result.expirationStr);
    } catch (err) {
      setCertError(err.message);
      setCertFile(null);
    }
  }

  async function handleKeyFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    setKeyError(null);
    setKeyValid(false);

    try {
      const text = await readFileAsText(file);
      validatePrivateKey(text);
      setKeyFile(file);
      setKeyValid(true);
    } catch (err) {
      setKeyError(err.message);
      setKeyFile(null);
    }
  }

  function isExpiringSoon(dateStr) {
    if (!dateStr) return false;
    const expDate = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  }

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    fields: [],
    payload: {},
  });

  async function handleSave() {
    const payload = {};
    const fields = [];

    // Only send changed fields (PATCH-like)
    if (clientId && clientId !== currentClientId) {
      payload.client_id = clientId;
      fields.push('client_id');
    }
    if (clientSecret) {
      payload.client_secret = clientSecret;
      fields.push('client_secret');
    }

    // Cert and key must be sent together
    if (certFile && !keyFile) {
      Notification({
        type: 'report',
        message: 'Certificado e chave privada devem ser enviados juntos',
        variant: 'contained',
        color: 'secondary',
      });
      return;
    }

    if (keyFile && !certFile) {
      Notification({
        type: 'report',
        message: 'Certificado e chave privada devem ser enviados juntos',
        variant: 'contained',
        color: 'secondary',
      });
      return;
    }

    // Read and send cert as base64 if a new file was selected
    if (certFile) {
      const certText = await readFileAsText(certFile);
      payload.certificado = btoa(certText);
      fields.push('certificado');
    }

    // Read and send key as base64 if a new file was selected
    if (keyFile) {
      const keyText = await readFileAsText(keyFile);
      payload.chave_privada = btoa(keyText);
      fields.push('chave_privada');
    }

    if (Object.keys(payload).length === 0) {
      Notification({
        type: 'info',
        message: 'Nenhuma alteração detectada',
        variant: 'contained',
        color: 'secondary',
      });
      return;
    }

    // Show confirmation dialog
    setConfirmDialog({
      open: true,
      fields,
      payload,
    });
  }

  async function confirmSave() {
    const { payload } = confirmDialog;
    setConfirmDialog({ open: false, fields: [], payload: {} });

    toggleLoading(layoutDispatch);
    try {
      if (configId) {
        await api.put(`/configuracoes-bancarias/${configId}`, payload);
      } else {
        // Create new config (first time setup)
        payload.banco = 'inter';
        await api.post('/configuracoes-bancarias', payload);
      }

      Notification({
        type: 'shipped',
        message: 'Configurações salvas com sucesso',
        variant: 'contained',
        color: 'success',
      });

      // Reload config to refresh status
      await loadConfig();

      // Clear file inputs
      setCertFile(null);
      setKeyFile(null);
      setClientSecret('');
      setCertExpiration(null);
      setKeyValid(false);
      setCertError(null);
      setKeyError(null);
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
    }
    toggleLoading(layoutDispatch);
  }

  function handleConfirmClose() {
    setConfirmDialog({ open: false, fields: [], payload: {} });
  }

  return (
    <>
      <PageTitle title="Configurações Bancárias" />
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget title="Banco Inter" disableWidgetMenu>
            <div className={classes.formContainer}>
              {/* Current Status */}
              {configId && (
                <div className={classes.statusCard}>
                  <Typography variant="subtitle2" gutterBottom>
                    Status Atual
                  </Typography>
                  <div className={classes.statusItem}>
                    <Typography variant="body2">Client ID:</Typography>
                    <Typography variant="body2">
                      {currentClientId || 'Não configurado'}
                    </Typography>
                  </div>
                  <div className={classes.statusItem}>
                    <Typography variant="body2">Client Secret:</Typography>
                    <Typography variant="body2">
                      {currentClientSecret || 'Não configurado'}
                    </Typography>
                  </div>
                  <div className={classes.statusItem}>
                    <Typography variant="body2">Certificado:</Typography>
                    <Typography
                      variant="body2"
                      className={
                        isExpiringSoon(currentCertExpiration)
                          ? classes.expirationWarning
                          : classes.expirationOk
                      }
                    >
                      {currentCertExpiration
                        ? `Expira em ${currentCertExpiration}${
                          isExpiringSoon(currentCertExpiration) ? ' ⚠️ Expirando em breve!' : ''
                        }`
                        : 'Não configurado'}
                    </Typography>
                  </div>
                  <div className={classes.statusItem}>
                    <Typography variant="body2">Chave Privada:</Typography>
                    <Typography variant="body2">
                      {currentKeyConfigured
                        ? 'Configurada'
                        : 'Não configurada'}
                    </Typography>
                  </div>
                </div>
              )}

              {/* Form Fields - 2x2 Grid Layout */}
              <Grid container spacing={3}>
                {/* Row 1: Client ID | Certificate */}
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.textField}
                    label="Client ID"
                    variant="outlined"
                    fullWidth
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder={currentClientId || '0689b4ee-fba9-4cd9-aa30-7b42c441785b'}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <div className={classes.fileInput}>
                    <input
                      accept=".crt,.pem"
                      id="cert-file"
                      type="file"
                      onChange={handleCertFileChange}
                    />
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="cert-file" className={classes.fileTextField}>
                      <span className={classes.fileTextFieldLabel}>Certificado (.crt)</span>
                      <span className={certFile ? classes.fileTextFieldValue : classes.fileTextFieldPlaceholder}>
                        {certFile ? certFile.name : 'Selecionar arquivo...'}
                      </span>
                    </label>
                    {certExpiration && (
                      <Typography
                        variant="caption"
                        className={
                          isExpiringSoon(certExpiration)
                            ? classes.expirationWarning
                            : classes.expirationOk
                        }
                        style={{ marginTop: 4, display: 'block' }}
                      >
                        Expira em:
                        {' '}
                        {certExpiration}
                      </Typography>
                    )}
                    {certError && (
                      <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                        {certError}
                      </Typography>
                    )}
                  </div>
                </Grid>

                {/* Row 2: Client Secret | Private Key */}
                <Grid item xs={12} md={6}>
                  <TextField
                    className={classes.textField}
                    label="Client Secret"
                    variant="outlined"
                    fullWidth
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="Deixe em branco para manter o atual"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <div className={classes.fileInput}>
                    <input
                      accept=".key,.pem"
                      id="key-file"
                      type="file"
                      onChange={handleKeyFileChange}
                    />
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="key-file" className={classes.fileTextField}>
                      <span className={classes.fileTextFieldLabel}>Chave Privada (.key)</span>
                      <span className={keyFile ? classes.fileTextFieldValue : classes.fileTextFieldPlaceholder}>
                        {keyFile ? keyFile.name : 'Selecionar arquivo...'}
                      </span>
                    </label>
                    {keyValid && (
                      <Typography
                        variant="caption"
                        className={classes.expirationOk}
                        style={{ marginTop: 4, display: 'block' }}
                      >
                        ✓ Formato válido
                      </Typography>
                    )}
                    {keyError && (
                      <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                        {keyError}
                      </Typography>
                    )}
                  </div>
                </Grid>
              </Grid>

              <div className={classes.buttonContainer}>
                <Button
                  className={classes.saveButton}
                  variant="contained"
                  size="large"
                  onClick={handleSave}
                >
                  Salvar Configurações
                </Button>
              </div>
            </div>
          </Widget>
        </Grid>
      </Grid>

      <ConfirmUpdateDialog
        open={confirmDialog.open}
        onClose={handleConfirmClose}
        onConfirm={confirmSave}
        fields={confirmDialog.fields}
      />
    </>
  );
}
